import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const faqs = [
  {
    q: "When does ApiaryKeeper launch?",
    a: "We're in beta testing right now, and the full launch is coming soon. Launch list members hear the date first — and get first crack at early access invites.",
  },
  {
    q: "Is the beta free?",
    a: "Yes. Beta testers use ApiaryKeeper free while we polish it together. If you said yes to beta testing on the pre-registration form, you're in the pool.",
  },
  {
    q: "How much will it cost at launch?",
    a: "Hobbyist is $4.99/month, Beekeeper is $9.99/month (coming soon), and Apiary Pro is $19.99/month. Pay annually and you get 12 months for the price of 10 — two months free, every year.",
  },
  {
    q: "What's a Founding Keeper?",
    a: "The first 100 launch list members to claim a spot. Founding Keepers lock in $2.99/month for Hobbyist or $12.99/month for Apiary Pro — forever — plus a badge, 50 BeeCredits™ on launch day, double referral rewards, early access to every new feature, and a spot in the Founding Keepers Hall if they want it.",
  },
  {
    q: "What are BeeCredits™?",
    a: "Our way of rewarding good beekeeping. You earn BeeCredits™ for consistent record-keeping and for qualified referrals, and you can put them toward your subscription and ApiaryKeeper features.",
  },
  {
    q: "What are Ask a Bee™ credits?",
    a: "Ask a Bee™ is our beekeeping-smart AI assistant. Each plan includes a monthly allowance of questions — 10 on Hobbyist, 50 on Beekeeper, and 200 on Apiary Pro.",
  },
  {
    q: "Does it work in the field without cell signal?",
    a: "Yes. ApiaryKeeper is offline-first thanks to KeeperSync™ — log inspections deep in the bee yard with no signal at all, and everything syncs when you're back in range.",
  },
  {
    q: "How does the referral program work?",
    a: "Every launch list member gets a personal referral code and link. When another beekeeper joins with your link and completes the qualifying steps, you earn BeeCredits™ — currently 50 per qualified referral. Hit milestones and you'll unlock achievements, all the way up to ApiaryKeeper Ambassador.",
  },
  {
    q: "Is my information private?",
    a: "We never sell your information, never share it with advertisers, and never ask for passwords or payment details by email. You can unsubscribe from marketing emails in one click, anytime.",
  },
];

export default function FAQ() {
  return (
    <section className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-testid="faq-page">
      <FadeUp>
        <p className="text-xs uppercase tracking-[0.3em] text-honey-dark mb-6">Questions, answered</p>
      </FadeUp>
      <h1 className="font-display font-medium tracking-tighter leading-[0.98] text-[clamp(2.4rem,5.5vw,4.5rem)] text-forest mb-4" data-testid="faq-heading">
        <MaskedLines
          lines={[
            <span key="a">Plain answers,</span>,
            <span key="b"><em className="text-honey-dark not-italic font-semibold">no buzzwords.</em></span>,
          ]}
          delay={0.2}
        />
      </h1>
      <FadeUp delay={0.6} className="mb-12">
        <p className="text-ink/60 leading-relaxed max-w-xl">
          Everything beekeepers ask us about the launch, pricing, BeeCredits™, and
          how ApiaryKeeper actually works.
        </p>
      </FadeUp>

      <FadeUp delay={0.2}>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} className="border-forest/15" data-testid={`faq-item-${i + 1}`}>
              <AccordionTrigger className="text-left font-display text-xl text-forest hover:text-honey-dark hover:no-underline py-5 transition-colors duration-300">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-ink/60 leading-relaxed text-sm md:text-base pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </FadeUp>

      <FadeUp delay={0.3} className="mt-14 text-center">
        <p className="text-ink/50 text-sm mb-5">Still curious about something?</p>
        <Link
          to="/pre-register?src=faq"
          data-testid="faq-cta"
          className="inline-flex items-center gap-2 bg-forest text-bone font-semibold px-8 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-honey hover:text-forest"
        >
          Join the Launch List
        </Link>
      </FadeUp>
    </section>
  );
}
